import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import type { TFunction } from 'i18next';
import type { TranslationNS } from '@gokrew/models/constants';
import type { MainLayoutRoute } from '@/routes/config';
import type { PermissionScope } from '@/types/permissions';

dayjs.extend(relativeTime);

/**
 * Converts a snake_case role string to a human-readable label.
 * Short segments (≤2 chars) are fully uppercased (e.g. "hr" → "HR").
 * Longer segments are title-cased (e.g. "admin" → "Admin").
 * Returns "---" for null/undefined.
 *
 * @example
 * formatRoleLabel('branch_manager') // "Branch Manager"
 * formatRoleLabel('hr_admin')       // "HR Admin"
 */
export function formatRoleLabel(role: string | null | undefined): string {
  if (!role) return '---';
  return role
    .split('_')
    .map((w) =>
      w.length <= 2 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)
    )
    .join(' ');
}

export const formatUrl = (url: string) => {
  return url?.replace(/^https?:\/\//, '');
};

export const removeEmptyFields = <T extends object>(obj: T): T => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== '' && value !== undefined && value !== null
    )
  ) as T;
};
export const formatDateTime = (
  t: TFunction<TranslationNS.Notes>,
  isoDate?: string,
  locale = 'en'
) => {
  if (!isoDate) return null;

  const date = new Date(isoDate);
  const now = new Date();

  // Normalize to remove time part (avoids edge bugs)
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfToday.getDate() - 1);

  const startOfGiven = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const time = date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (startOfGiven.getTime() === startOfToday.getTime()) {
    return time;
  }

  if (startOfGiven.getTime() === startOfYesterday.getTime()) {
    return `${t('view.yesterday')}, ${time}`;
  }

  const isSameYear = date.getFullYear() === now.getFullYear();

  return `${date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    ...(isSameYear ? {} : { year: 'numeric' }),
  })}, ${time}`;
};

/** Width of dayjs' "a few seconds" bucket — below it, everything reads as "just now". */
const JUST_NOW_THRESHOLD_SECONDS = 45;

export function formatRecentChatTime(
  updatedAtSeconds: number,
  locale: string = 'en',
  justNowLabel?: string
): string {
  const nowSeconds = Date.now() / 1000;
  // The timestamp comes off the chatbot host's clock but is rendered against
  // the browser's. A host running even a second ahead would otherwise put a
  // just-saved thread in the future, which dayjs renders as "in a few
  // seconds" — clamping to now keeps the label skew-proof.
  const safeSeconds = Math.min(updatedAtSeconds, nowSeconds);
  if (justNowLabel && nowSeconds - safeSeconds < JUST_NOW_THRESHOLD_SECONDS) {
    return justNowLabel;
  }
  return dayjs.unix(safeSeconds).locale(locale).fromNow();
}

export const getMonthAbbreviations = (locale: string = 'en'): string[] => {
  const months: string[] = [];
  for (let i = 0; i < 12; i++) {
    months.push(dayjs().locale(locale).month(i).format('MMM').toUpperCase());
  }
  return months;
};

export interface RouteMapEntry {
  scope: PermissionScope | PermissionScope[] | undefined;
  redirectTo: string;
  // True for routes that must redirect on company switch even on a literal path match (dynamic detail pages, "create" forms).
  redirectOnSwitch: boolean;
}

type RouteScope = NonNullable<MainLayoutRoute['scope']>;

interface RouteConfig {
  path?: MainLayoutRoute['path'];
  index?: boolean;
  scope?: MainLayoutRoute['scope'];
  children?: readonly RouteConfig[];
  redirectOnCompanySwitch?: MainLayoutRoute['redirectOnCompanySwitch'];
}

export function getRouteMap(
  routes: readonly MainLayoutRoute[]
): Map<string, RouteMapEntry> {
  const out = new Map<string, RouteMapEntry>();

  (function flatRoute(
    routes: readonly RouteConfig[],
    parent: string,
    pScope?: RouteScope,
    sectionRoot?: string
  ) {
    for (const r of routes) {
      if (!r?.path || r.index) continue;
      const full = parent ? `${parent}/${r.path}` : r.path;
      const scope = r.scope ?? pScope;

      const isDynamic = full.includes(':');
      const isCreate = full.split('/').pop() === 'create'; // e.g. "assets/create" — a create form, not a listing/detail page
      const root = sectionRoot ?? full.split('/')[0];

      // Dynamic routes can't redirect to themselves (unknown param) — use root instead.
      // "create" routes redirect to their listing page (the path minus the trailing segment).
      // Routes explicitly flagged redirectOnCompanySwitch (no auto-detected pattern) fall back to root.
      const redirectTo =
        isDynamic || r.redirectOnCompanySwitch
          ? `/${root}`
          : isCreate
            ? `/${full.slice(0, -'/create'.length)}`
            : `/${full}`;

      out.set(`/${full}`, {
        scope,
        redirectTo,
        redirectOnSwitch: isDynamic || isCreate || !!r.redirectOnCompanySwitch,
      });
      if (r.children) flatRoute(r.children, full, scope, root);
    }
  })(routes, '', undefined, undefined);

  return out;
}
